import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../../../contexts/auth0-context'
import callAPI from '../../../../../API/callSecureAPI'
import endpoints from '../../../../../API/endpoints'
import { TppCell, TppRow, TppSlot } from '../../../../../components/TppView/types'
import { useInvalidateTPPChangeLogQuery } from '../../../../../components/TPPChangeLog/queries/useInvalidateTPPChangeLogQuery'

type DeleteTPPCellPayload = {
    cellId: string
    tppId: string
}

type UseDeleteTPPCellMutationProps = {
    onSuccess?: () => void
}

export const useDeleteTPPCellMutation = ({ onSuccess }: UseDeleteTPPCellMutationProps = {}) => {
    const [controller, setController] = useState<AbortController | null>(null)
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()
    const { invalidateTPPChangeLogQuery } = useInvalidateTPPChangeLogQuery()

    const deleteTPPCell = async ({ cellId, tppId }: DeleteTPPCellPayload) => {
        console.log('Deleting TPP cell with cellId:', cellId)
        try {
            if (controller) {
                controller.abort()
                setController(null)
            }

            const abortController = new AbortController()
            setController(abortController)

            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const { url, method } = endpoints.deleteTPPCell

            const finalUrl = `${url}/${cellId}/delete`

            const response = await callAPI(finalUrl, method, token, undefined, { signal: abortController.signal })

            return response.data
        } catch (e) {
            console.error('Error deleting TPP cell', e)
            throw e
        }
    }

    return useMutation(deleteTPPCell, {
        onSuccess: async (data, variables) => {
            const { row } = data
            let deletedSlotId: string | null = null

            queryClient.setQueryData<TppCell[]>(['tppCells', variables.tppId], (old) => {
                if (!old) return []
                const cell = old.find((c) => c._id === variables.cellId)
                deletedSlotId = cell?.slotId ?? null
                return old.filter((c) => c._id !== variables.cellId)
            })

            if (row) {
                queryClient.setQueryData<TppRow[]>(['tppRows', variables.tppId], (old) => {
                    if (!old) return []
                    return old.map((r) => (r._id === row._id ? row : r))
                })
                if (deletedSlotId) {
                    queryClient.setQueryData<TppSlot[]>(['tppSlots', variables.tppId], (old) => {
                        if (!old) return []
                        return old.filter((s) => s._id !== deletedSlotId)
                    })
                }
            }

            invalidateTPPChangeLogQuery(variables.tppId)
            onSuccess?.()
        },
    })
}
